import { handleError } from '@risksmart-app/components/src/utils/errorUtils';
import {
  shouldRetryTrpcQuery,
  trpcQueryRetryDelayMs,
} from '@risksmart-app/components/src/utils/trpc/retryPolicy';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCContext } from '@trpc/tanstack-react-query';

import { shouldReportTrpcQueryError } from './trpcUtils';

let browserQueryClient: QueryClient | undefined;

/**
 * Exported so the retry defaults can be asserted against the real config
 * rather than a hand-built client.
 */
export const makeQueryClient = () => {
  return new QueryClient({
    // tanstack-query swallows query errors unless a consumer reads isError —
    // mirror the Apollo errorLink so silent failures still reach Sentry. The
    // portal previously had no reporter at all, so EVERY tRPC query failure
    // (taxonomy, response list, response detail) was invisible where the
    // Apollo branch logged it. Mirrors web's reporter (RSP-6539) —
    // packages/web/src/utils/trpc.ts — including its exclusions: mutations are
    // omitted (the form owns mutation error UX) and only the procedure path is
    // captured, never the full query key, which embeds procedure input.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!shouldReportTrpcQueryError(error, query)) {
          return;
        }

        handleError(error, {
          tags: { source: 'trpc-query' },
          extra: { procedurePath: query.queryKey[0] },
        });
      },
    }),
    defaultOptions: {
      queries: {
        // Same stale-while-revalidate defaults web ships (COI-451) — see
        // packages/web/src/utils/trpc.ts. `staleTime: 0` keeps every mount
        // revalidating (so this is not the Apollo `no-cache` policy's
        // staleness behaviour being relaxed), while `gcTime > 0` lets a
        // remount render the previous response instantly instead of a blank
        // spinner. The earlier `gcTime: 0` here was a straight `no-cache`
        // emulation, i.e. the half of COI-451 web deliberately reversed.
        // `queryClient.clear()` on auth change (TrpcProvider) is what keeps
        // cached data from crossing session boundaries.
        gcTime: 5 * 60 * 1000, // Keep unused query data for 5 minutes
        staleTime: 0, // Always consider data stale
        refetchOnMount: true, // Always refetch when component mounts
        refetchOnWindowFocus: false, // Don't refetch on window focus by default
        // Retry only what is transient at the browser's own hop — a 429, an
        // edge 5xx with no tRPC envelope, a dead connection, auth still
        // settling. Everything else is terminal, because the tRPC tier has
        // already retried its own upstream (COI-159).
        //
        // Until this, the portal ran react-query's defaults: three retries on
        // 1s/2s/4s backoff for EVERY failure, so a 403 on a respondent's
        // questionnaire went out four times and a 429 came straight back inside
        // the limiter's own window. Same rules as web
        // (packages/web/src/utils/trpc.ts) because it is the same hop, and they
        // are shared rather than copied so the two cannot drift again.
        retry: shouldRetryTrpcQuery,
        retryDelay: trpcQueryRetryDelayMs,
      },
      mutations: {
        // Never retried, at any layer. The V3 write path carries no idempotency
        // key, so a retry after a response that was lost in transit — the exact
        // case a retry is for — can duplicate the record it just created
        // (COI-159). The portal writes questionnaire responses and file
        // uploads over this client. react-query's own default is already 0
        // attempts; this is explicit so it survives someone raising the global
        // default.
        retry: false,
      },
    },
  });
};

export const getQueryClient = () => {
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
};

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>();
